import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle = () => {
  const { t } = useTranslation('common');
  const { themePreference, setThemePreference, PREFERENCE_LIGHT, PREFERENCE_DARK, PREFERENCE_SYSTEM } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const options = [
    { value: PREFERENCE_LIGHT, labelKey: 'themeLight', icon: 'light_mode' },
    { value: PREFERENCE_DARK, labelKey: 'themeDark', icon: 'dark_mode' },
    { value: PREFERENCE_SYSTEM, labelKey: 'themeSystem', icon: 'settings_brightness' },
  ];

  const currentOption = options.find((o) => o.value === themePreference) ?? options[2];
  const currentLabel = t(currentOption.labelKey);
  const currentIcon = currentOption.icon;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-surface text-text-secondary hover:text-primary hover:border-primary/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-surface"
        aria-label={t('themePreference')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-describedby="theme-toggle-desc"
      >
        <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
          {currentIcon}
        </span>
        <span className="text-sm font-medium hidden sm:inline">{currentLabel}</span>
        <span className="material-symbols-outlined text-[18px] transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} aria-hidden="true">
          expand_more
        </span>
      </button>
      <p id="theme-toggle-desc" className="sr-only">
        {t('themeDescription')}
      </p>
      {isOpen && (
        <ul
          className="absolute right-0 top-full mt-1.5 min-w-[140px] py-1 rounded-lg border border-border bg-surface shadow-lg z-50"
          role="listbox"
          aria-label={t('themeOptions')}
        >
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={themePreference === opt.value}>
              <button
                type="button"
                onClick={() => {
                  setThemePreference(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/50 ${
                  themePreference === opt.value
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-main hover:bg-slate-100 dark:hover:bg-slate-700/50'
                }`}
              >
                <span className="material-symbols-outlined text-[20px]">{opt.icon}</span>
                {t(opt.labelKey)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ThemeToggle;
